<template>
  <div :class="{ money__inline: inline }">{{ formatted }}</div>
</template>

<script setup>
import { computed } from 'vue'
import { useShared } from '@/helpers/vueHelpers.js'
import CURRENCIES from '@/dataset/currencies.json'

const props = defineProps({
  currency: String,
  value: Number,
  inline: { type: Boolean, default: false },
  subunitsValue: { type: Boolean, default: true },
  alwaysShowDecimals: { type: Boolean, default: false },
  formatLocale: { type: String, default: null },
  options: { type: Object, default: () => ({ currencyDisplay: 'code' }) },
})

const { locale } = useShared()

const hideSubunits = computed(() => {
  if (props.alwaysShowDecimals) return false

  return props.value.toFixed().slice(-2) === '00' || Number(props.value) === 0
})

const formatted = computed(() => {
  let value = props.value
  if (Number.isFinite(value)) {
    try {
      let formatOptions = { style: 'currency', currency: props.currency }
      let numFormat = new Intl.NumberFormat(locale, formatOptions)
      let { minimumFractionDigits } = numFormat.resolvedOptions()
      if (props.subunitsValue) {
        const { exponent } = CURRENCIES[props.currency]
        value = value / 10 ** (exponent === undefined ? minimumFractionDigits : exponent)
      }
      if (props.alwaysShowDecimals) {
        const exponent = CURRENCIES[props.currency]?.exponent ?? minimumFractionDigits ?? 2
        formatOptions = {
          ...formatOptions,
          minimumFractionDigits: exponent,
          maximumFractionDigits: exponent,
        }
      } else if (hideSubunits.value) {
        formatOptions = {
          style: 'currency',
          currency: props.currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }
      }
      formatOptions = {
        ...formatOptions,
        ...props.options,
      }
      numFormat = new Intl.NumberFormat(props.formatLocale || locale, formatOptions)
      return numFormat.format(value)
    } catch (err) {
      return err.message
    }
  } else {
    return '#NaN!'
  }
})
</script>

<style lang="scss" scoped>
.money__inline {
  display: inline-block;
}
</style>
